import { Component, Vue } from "vue-property-decorator";
import UlContentHeader from "@/components/UlContentHeader.vue";
import UlBreadcrumbs from "@/components/UlBreadcrumbs.vue";
import UIDataTable from "@/components/UIDataTable.vue";
import { DEFAULT_TABLE_OPTIONS, TableOptions } from "@/api/request";
import { SearchRequest } from "@/api/ticket-order-shop/request";
import { SearchItem } from "@/api/ticket-order-shop/response";
import TicketOrderShopGet from "@/store/ticket-order-shop/get";
import TicketOrderShopSearch from "@/store/ticket-order-shop/search";
import Flash, { ErrorAlert } from "@/store/common/flash";
import Loading from "@/store/loading";

@Component({
  components: { UlContentHeader, UlBreadcrumbs, UIDataTable }
})
export default class Index extends Vue {
  // ------------
  // 固定値
  // ------------
  // タイトル
  headingMain = "チケット受注管理";
  headingSub = "Ticket order management";
  breadCrumbs = [{ text: "店舗一覧", disabled: true }];
  // テーブルヘッダ（UIDataTableコンポーネントに渡す）
  tableHeaders = [
    { text: "店舗ID", value: "id" },
    { text: "店舗名", value: "name" },
    { text: "", value: "space", sortable: false },
    { text: "", value: "custom", sortable: false }
  ];

  menuProps = {
    closeOnClick: false,
    closeOnContentClick: false,
    disableKeys: true,
    openOnClick: false,
    maxHeight: 304
  };

  // ------------
  // 変動値
  // ------------

  // 検索入力オプション
  inputOptions = {
    searchShopId: null as number | null,
    selectShopIds: null as number[] | null
  };

  // テーブル検索オプション（UIDataTableコンポーネントに渡す）
  tableOptions = DEFAULT_TABLE_OPTIONS;

  // ------------

  get loading() {
    return Loading.isLoading;
  }

  /**
   * テーブルに表示するアイテムリスト（UIDataTableコンポーネントに渡す）
   */
  get tableItems() {
    return TicketOrderShopSearch.getItems;
  }

  /**
   * 総件数（UIDataTableコンポーネントに渡す）
   */
  get totalCount() {
    return TicketOrderShopSearch.getTotalCount;
  }

  get shopItems() {
    return TicketOrderShopGet.getItems;
  }

  /**
   * createdライフサイクルフック（UIDataTableコンポーネントに渡す）
   */
  async created() {
    await TicketOrderShopSearch.restore(this.inputOptions as SearchRequest);
    const request = TicketOrderShopSearch.getSearchRequest;
    // 検索入力オプション
    request.searchShopId &&
      (this.inputOptions.searchShopId = request.searchShopId);
    request.selectShopIds &&
      (this.inputOptions.selectShopIds = request.selectShopIds);

    // 検索テーブルオプション
    request.page && (this.tableOptions = request);
    await this.search();
  }

  /**
   * beforeDestroyライフサイクルフック
   */
  async beforeDestroy() {
    await TicketOrderShopGet.clearResponse();
    await TicketOrderShopSearch.clearResponse();
  }

  /**
   * ページング変更コールバック
   *
   * @param tableOptions TableOptions
   */
  async tableChangeCallback(tableOptions: TableOptions) {
    // 子コンポーネントへpagingOptionsの変更が通知される
    this.tableOptions = tableOptions;
    await this.search();
  }

  /**
   * 検索ボタンがクリックされた際のコールバック
   */
  async searchClickCallback() {
    await Flash.clear();
    await this.search();
  }

  /**
   * テーブル行の確認ボタンがクリックされた際のコールバック
   *
   * @param item 選択行のAdminItem
   */
  async customClickCallback(item: SearchItem) {
    await this.$router.push({
      name: "ticket-order-management-list",
      params: { id: item.id },
      query: { shopName: item.name }
    });
  }

  /**
   * 店舗情報の一覧を取得する処理
   */
  async fetchShopList(): Promise<boolean> {
    if (TicketOrderShopGet.isSuccess) {
      return true;
    }

    await TicketOrderShopGet.get();
    if (!TicketOrderShopGet.isSuccess) {
      await Flash.setErrorNow({
        message: TicketOrderShopGet.getMessage,
        showReloadButton: false
      } as ErrorAlert);
    }
    return TicketOrderShopGet.isSuccess;
  }

  /**
   * 検索処理
   */
  async search() {
    let isSuccess = await this.fetchShopList();
    if (!isSuccess) {
      return;
    }

    let request = this.createRequest();
    await TicketOrderShopSearch.search(request);
    if (!TicketOrderShopSearch.isSuccess) {
      await Flash.setErrorNow({
        message: TicketOrderShopSearch.getMessage,
        showReloadButton: true
      } as ErrorAlert);
    }
  }

  /**
   * dataから検索用のリクエストを作成する
   */
  private createRequest(): SearchRequest {
    const request = this.tableOptions as SearchRequest;
    if (this.inputOptions.searchShopId) {
      request.searchShopId = this.inputOptions.searchShopId;
    } else {
      delete request.searchShopId;
    }

    if (this.inputOptions.selectShopIds) {
      request.selectShopIds = this.inputOptions.selectShopIds;
    } else {
      delete request.selectShopIds;
    }

    return request;
  }
}
